'use client';

import { useEffect, useId, useRef, useState } from 'react';
import styles from './ui.module.css';

/**
 * A dropdown that belongs to this brand.
 *
 * **A native `<select>` cannot be one, and that is the whole reason this file
 * exists.** The closed control is styleable and the open list is not: the popup
 * is drawn by the operating system, outside the page, and `background` and
 * `color` on an `<option>` are ignored outright on macOS and honoured
 * inconsistently on Windows. Every surface in this product is dark — the ground
 * is Ink or one of the three channel grounds — so every one of the five
 * dropdowns in the app opened a light system menu in the platform's own font.
 * They also arrived with no class at all, so even closed they were the browser
 * default sitting beside `.input` fields that are square, sunken and set in
 * Archivo.
 *
 * So the list is drawn in the page, and everything a `<select>` gave away for
 * free is written out here rather than assumed:
 *
 * — **It is still a combobox to anything that is not a mouse.** The ARIA
 *   combobox/listbox pattern, the full key set, and the selected option
 *   announced by name. A custom dropdown that is a `<div>` with a click handler
 *   is not a cheaper version of this control, it is an unusable one.
 * — **It still submits.** Two screens read their values with
 *   `new FormData(form)` rather than from React state, so a `name` renders a
 *   hidden input carrying the value. Dropping that would have made the creator
 *   application post `platform: null` with nothing on screen to suggest why.
 *
 * **Focus stays on the trigger the whole time**, and the active option is named
 * by `aria-activedescendant` rather than focused. That is the pattern a native
 * select uses, and it is the one that survives the list being closed under a
 * screen reader: moving real focus into the popup means every close has to put
 * it back, and the version that forgets sends focus to the top of the document.
 *
 * **The storefront does not use this.** Its publishing form is pinned to work
 * with no JavaScript at all — the storefront's rule, since retired — so its
 * one dropdown stays a real `<select>` wearing `.selectNative`, which is the
 * same box without the list. A control that needs a browser has no business on
 * the page that asks studios to take us seriously.
 */

export interface SelectOption {
  value: string;
  label: string;
  /** Rendered, announced, and not choosable. */
  disabled?: boolean | undefined;
}

export interface SelectProps {
  options: readonly SelectOption[];
  /** Controlled. A value matching no option shows the placeholder. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Submitted with the surrounding form, through a hidden input.
   *
   * Only needed by the screens that read `new FormData(form)`, but harmless
   * elsewhere and cheaper than remembering which screens those are.
   */
  name?: string | undefined;
  disabled?: boolean | undefined;
  /** What the trigger reads when nothing is chosen. */
  placeholder?: string | undefined;
  /**
   * The accessible name, when there is no `<label>` around this.
   *
   * A wrapping `<label>` does work and is the better answer where a form
   * already has one: `<button>` is a labelable element, so the label names the
   * trigger and clicking its text opens the list, exactly as it would with a
   * `<select>`. The hidden input cannot steal that association — an input of
   * type `hidden` is not labelable.
   *
   * Passing `aria-label` where a visible label already exists would *replace*
   * the name a voice-control user reads on screen, so these are for the places
   * with no label to associate — a filter row, a cell in a table.
   */
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
  /**
   * For a row that wants a different width from the default.
   *
   * `minWidth` rather than `width`: the wrapper shrinks to its trigger, and a
   * trigger sized to whichever option is *currently* chosen would change width
   * every time somebody picked a different one. The floor in `.select` is what
   * holds it still, so a caller that wants a narrower control lowers the floor
   * — the price form does, because three-letter currency codes beside two
   * 8rem fields do not want ten rems of well.
   */
  style?: React.CSSProperties | undefined;
}

/** How long two keystrokes count as one word for the type-ahead. */
const TYPEAHEAD_MS = 500;

export function Select({
  options,
  value,
  onChange,
  name,
  disabled = false,
  placeholder = 'Choose…',
  style,
  ...labels
}: SelectProps): React.ReactElement {
  const base = useId();
  const listId = `${base}-list`;
  const optionId = (index: number): string => `${base}-option-${index}`;

  const chosen = options.findIndex((option) => option.value === value);
  const selected = chosen === -1 ? null : options[chosen];

  const [open, setOpen] = useState(false);
  /**
   * Which option the keyboard is on, which is not the same as which is chosen.
   *
   * A native select moves a highlight through the list and only commits on
   * Enter, so arrowing past four options does not fire four changes — which
   * here would be four writes to the catalogue.
   */
  const [active, setActive] = useState(chosen === -1 ? 0 : chosen);

  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });

  /**
   * Anywhere else, and the list closes.
   *
   * `pointerdown` rather than `click`: a click on another button would
   * otherwise run that button's handler with this list still open over it, and
   * on a page of admin decisions that is a second control being pressed through
   * a popup.
   */
  useEffect(() => {
    if (!open) return;

    const dismiss = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  /**
   * Keep the active option on screen.
   *
   * `block: 'nearest'` scrolls only when it has to, so arrowing down a short
   * list does not jump the page under someone who can already see the option
   * they are moving onto.
   */
  useEffect(() => {
    if (!open) return;
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  function show(): void {
    // An empty list stays shut. Opening one would put an `<ul>` with nothing in
    // it on screen and leave `aria-activedescendant` naming an id that is not
    // in the document, which is worse than a control that does not respond.
    if (disabled || options.length === 0) return;
    setActive(chosen === -1 ? 0 : chosen);
    setOpen(true);
  }

  function commit(index: number): void {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
  }

  /** An index brought back into range, so every walk below can wrap. */
  function wrap(index: number): number {
    const count = options.length;
    return ((index % count) + count) % count;
  }

  /** The next selectable option in a direction, wrapping at both ends. */
  function step(from: number, direction: 1 | -1): number {
    for (let moved = 1; moved <= options.length; moved += 1) {
      const index = wrap(from + direction * moved);
      if (!options[index]?.disabled) return index;
    }
    return from;
  }

  /**
   * Typing a letter jumps to an option starting with it, as a select does.
   *
   * Worth the lines: the currency dropdown is the whole ISO list, and without
   * this `EUR` is a dozen presses of the down arrow.
   *
   * A second letter inside the window extends the search rather than starting
   * a new one, which is what separates `PL` from "P, then L". The search
   * therefore begins *at* the current option while a word is being typed and
   * *after* it when a word is beginning — so pressing `C` repeatedly cycles
   * through the options starting with C rather than sticking on the first.
   */
  function jump(character: string): void {
    const now = Date.now();
    const continuing = now - typed.current.at <= TYPEAHEAD_MS;
    const text = (continuing ? typed.current.text : '') + character;
    typed.current = { text, at: now };

    const from = open ? active : chosen === -1 ? 0 : chosen;
    const offset = text.length > 1 ? 0 : 1;

    for (let moved = 0; moved < options.length; moved += 1) {
      const index = wrap(from + offset + moved);
      const option = options[index];
      if (!option || option.disabled) continue;
      if (option.label.toLowerCase().startsWith(text.toLowerCase())) {
        if (open) setActive(index);
        else commit(index);
        return;
      }
    }
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        if (!open) show();
        else setActive((index) => step(index, direction));
        return;
      }
      case 'Home':
      case 'End': {
        if (!open) return;
        event.preventDefault();
        setActive(step(event.key === 'Home' ? -1 : options.length, event.key === 'Home' ? 1 : -1));
        return;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (open) commit(active);
        else show();
        return;
      }
      case 'Escape': {
        if (!open) return;
        event.preventDefault();
        setOpen(false);
        return;
      }
      case 'Tab': {
        // Not prevented: Tab must still leave. Closing first means the list is
        // not left hanging over whatever gets focus next.
        setOpen(false);
        return;
      }
      default: {
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          jump(event.key);
        }
      }
    }
  }

  return (
    <div
      ref={root}
      className={styles.select}
      style={style}
      /*
       * Focus leaving the whole control closes it. Checked against
       * `relatedTarget` rather than closing on any blur, because the list's own
       * elements are inside this subtree and a plain blur handler would shut
       * the list the instant it opened.
       */
      onBlur={(event) => {
        if (!root.current?.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      {name === undefined ? null : <input type="hidden" name={name} value={value} />}

      <button
        type="button"
        role="combobox"
        className={styles.selectTrigger}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-activedescendant={open ? optionId(active) : undefined}
        {...labels}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? styles.selectValue : styles.selectPlaceholder}>
          {selected ? selected.label : placeholder}
        </span>
        {/*
          The one mark on the control. A chevron rather than a filled triangle:
          the geometry here is square and hard-edged, and a solid arrowhead is
          the one shape on the page that would read as a platform widget.
        */}
        <svg className={styles.selectMark} viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {/*
        Rendered only while open, rather than hidden.

        A list that is always in the tree is a list a screen reader can reach
        with its own cursor while the control says it is collapsed, and it is
        also six hundred ISO currency codes in the document on every admin page.
      */}
      {open && (
        <ul
          ref={list}
          id={listId}
          role="listbox"
          className={styles.selectList}
          /*
           * Keeps focus on the trigger through a click. Without this the
           * mousedown blurs the button, `onBlur` closes the list, and the click
           * lands on whatever the list was covering.
           */
          onMouseDown={(event) => event.preventDefault()}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled === true ? true : undefined}
              className={styles.selectOption}
              data-active={index === active ? '' : undefined}
              onClick={() => commit(index)}
              onMouseEnter={() => setActive(index)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Options from a list of plain values, which is what most callers have.
 *
 * `['draft', 'coming_soon']` becomes `draft` and `coming soon`. Every one of
 * these lists is a database enum, and every call site was already writing the
 * same `replace(/_/g, ' ')` into its own `<option>` — four copies of one
 * decision about how an enum is spelled to a person.
 */
export function selectOptions(values: readonly string[]): SelectOption[] {
  return values.map((value) => ({ value, label: value.replace(/_/g, ' ') }));
}
